/**
 * Apple IAP Bridge — WebView ↔ Native (StoreKit 2) communication layer.
 *
 * All communication uses window.ReactNativeWebView.postMessage (WebView → Native)
 * and window.addEventListener('message', ...) (Native → WebView).
 *
 * Message protocol:
 *   WebView → Native:
 *     { type: 'IAP_PURCHASE', productId: string }
 *     { type: 'IAP_GET_PRODUCTS', productIds: string[] }
 *     { type: 'IAP_RESTORE' }
 *     { type: 'IAP_FINISH', transactionId: string }
 *
 *   Native → WebView:
 *     { type: 'IAP_TRANSACTION', jwsTransaction: string, productId: string, transactionId: string }
 *     { type: 'IAP_ERROR', code: string, message: string }
 *     { type: 'IAP_PRODUCTS', products: AppleProduct[] }
 *     { type: 'IAP_RESTORE_RESULT', transactions: [...] }
 */

export interface AppleProduct {
  productId: string;
  localizedPrice: string;
  price: number;
  currency: string;
  title: string;
  description: string;
}

export interface IAPTransactionResult {
  jwsTransaction: string;
  productId: string;
  transactionId: string;
}

type MessageHandler = (event: MessageEvent) => void;

// Pending promise resolvers keyed by request type
const pendingRequests: Map<string, { resolve: (value: any) => void; reject: (error: any) => void }> = new Map();
let messageListenerAttached = false;

/** Attach the global message listener (once). */
function ensureMessageListener(): void {
  if (messageListenerAttached) return;
  messageListenerAttached = true;

  const handler: MessageHandler = (event: MessageEvent) => {
    let data: any;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return; // Not a JSON message
    }

    if (!data || !data.type) return;

    switch (data.type) {
      case 'IAP_TRANSACTION': {
        const pending = pendingRequests.get('IAP_PURCHASE');
        if (pending) {
          pendingRequests.delete('IAP_PURCHASE');
          pending.resolve({
            jwsTransaction: data.jwsTransaction,
            productId: data.productId,
            transactionId: data.transactionId,
          } as IAPTransactionResult);
        }
        break;
      }
      case 'IAP_ERROR': {
        const pending = pendingRequests.get('IAP_PURCHASE');
        if (pending) {
          pendingRequests.delete('IAP_PURCHASE');
          const err = new Error(data.message || 'Apple IAP purchase failed');
          (err as any).code = data.code || 'PURCHASE_FAILED';
          pending.reject(err);
        }
        break;
      }
      case 'IAP_PRODUCTS': {
        const pending = pendingRequests.get('IAP_GET_PRODUCTS');
        if (pending) {
          pendingRequests.delete('IAP_GET_PRODUCTS');
          pending.resolve(data.products as AppleProduct[]);
        }
        break;
      }
      case 'IAP_RESTORE_RESULT': {
        const pending = pendingRequests.get('IAP_RESTORE');
        if (pending) {
          pendingRequests.delete('IAP_RESTORE');
          pending.resolve(data.transactions || []);
        }
        break;
      }
      default:
        break;
    }
  };

  window.addEventListener('message', handler);
}

/** Send a message to the native React Native WebView. */
function postToNative(message: Record<string, unknown>): void {
  const rnWebView = (window as any).ReactNativeWebView;
  if (rnWebView && typeof rnWebView.postMessage === 'function') {
    rnWebView.postMessage(JSON.stringify(message));
  } else {
    console.warn('[AppleIAPBridge] ReactNativeWebView not available');
  }
}

/**
 * Request Apple IAP products with pricing from StoreKit.
 *
 * @param productIds - Apple product IDs to fetch
 * @returns Array of products with localized pricing
 */
export function requestProducts(productIds: string[]): Promise<AppleProduct[]> {
  ensureMessageListener();

  return new Promise((resolve, reject) => {
    // Set a timeout for the request
    const timeout = setTimeout(() => {
      pendingRequests.delete('IAP_GET_PRODUCTS');
      reject(new Error('IAP product request timed out'));
    }, 15000);

    pendingRequests.set('IAP_GET_PRODUCTS', {
      resolve: (value) => {
        clearTimeout(timeout);
        resolve(value);
      },
      reject: (error) => {
        clearTimeout(timeout);
        reject(error);
      },
    });

    postToNative({ type: 'IAP_GET_PRODUCTS', productIds });
  });
}

/**
 * Initiate an Apple IAP purchase via StoreKit.
 *
 * @param productId - Apple product ID to purchase
 * @param appAccountToken - Optional user UUID to link this transaction to a user account.
 *   Apple includes this in the JWS transaction and ONE_TIME_CHARGE webhook payload.
 * @returns Transaction result with JWS token
 */
export function purchase(productId: string, appAccountToken?: string): Promise<IAPTransactionResult> {
  ensureMessageListener();

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      pendingRequests.delete('IAP_PURCHASE');
      reject(new Error('IAP purchase timed out'));
    }, 120000); // 2 minute timeout for purchase flow

    pendingRequests.set('IAP_PURCHASE', {
      resolve: (value) => {
        clearTimeout(timeout);
        resolve(value);
      },
      reject: (error) => {
        clearTimeout(timeout);
        reject(error);
      },
    });

    postToNative({ type: 'IAP_PURCHASE', productId, appAccountToken });
  });
}

/**
 * Signal native side to finish a transaction after backend confirms.
 *
 * @param transactionId - The transaction ID to finish
 */
export function finishTransaction(transactionId: string): void {
  postToNative({ type: 'IAP_FINISH', transactionId });
}

/**
 * Trigger restore purchases flow.
 * Returns active subscriptions with JWS tokens for backend verification.
 *
 * @returns Array of restored transactions with JWS data
 */
export function restorePurchases(): Promise<IAPTransactionResult[]> {
  ensureMessageListener();

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      pendingRequests.delete('IAP_RESTORE');
      reject(new Error('IAP restore timed out'));
    }, 30000);

    pendingRequests.set('IAP_RESTORE', {
      resolve: (value) => {
        clearTimeout(timeout);
        resolve(value);
      },
      reject: (error) => {
        clearTimeout(timeout);
        reject(error);
      },
    });

    postToNative({ type: 'IAP_RESTORE' });
  });
}

/**
 * Open the native iOS subscription management screen (Settings → Subscriptions).
 * Fire-and-forget — no response expected from the native side.
 */
export function manageSubscriptions(): void {
  postToNative({ type: 'IAP_MANAGE_SUBSCRIPTIONS' });
}
